import {
  MediaController,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlayButton,
  MediaMuteButton,
  MediaFullscreenButton,
  // MediaPipButton,
} from 'media-chrome/react';
import ReactPlayer from 'react-player';
import { Box, useStyle, Svg, Button } from '@altermaind/web/ui';
import { VideoPlayerMainContainer, VideoPlayerPanelControlsOverlay } from './styles';
import { usePlayer } from './usePlayer';

type Props = {
  src: string;
  startFrom?: number;
  onCloseVideo: () => void;
  onVideoEnd?: () => void;
  onVideoProgress?: (activeSecond: number) => void;
  onCancelCloseVideoReady?: (handleCancelExit: () => void) => void;
  resetOnEnd?: boolean;
};

export const Player = ({
  src,
  startFrom,
  onCloseVideo,
  onVideoEnd,
  onCancelCloseVideoReady,
  onVideoProgress,
  resetOnEnd,
}: Props) => {
  const {
    containerRef,
    videoRef,
    reactPlayerRef,
    mediaControllerRef,
    isMobileOrTablet,
    playing,
    showControls,
    handleVideoReady,
    onReactPlayerReady,
    handleCloseVideo,
    handleMouseMove,
    onEnded,
    onProgress,
  } = usePlayer({
    startFrom,
    onCloseVideo,
    onVideoEnd,
    onCancelCloseVideoReady,
    onVideoProgress,
    resetOnEnd,
  });

  const wrapperControlsStyle = useStyle({
    px: 's',
    py: 'xs',
  });

  const controlsDefaultStyle = useStyle({
    background: 'transparent',
  });

  // On iOS/mobile use native video (iOS supports HLS natively)
  if (isMobileOrTablet) {
    return (
      <VideoPlayerMainContainer ref={containerRef}>
        <Box position='absolute' top={0} right={0} zIndex={10} p='s'>
          <Button onClick={handleCloseVideo} variant='link/primary'>
            <Svg name='close' size='m' />
          </Button>
        </Box>
        <video
          ref={videoRef}
          src={src}
          controls
          disablePictureInPicture
          controlsList='nodownload noremoteplayback'
          width='100%'
          height='100%'
          onEnded={onEnded}
          onLoadedMetadata={handleVideoReady}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          // eslint-disable-next-line react/no-unknown-property
          x-webkit-airplay='deny'
          onTimeUpdate={onProgress}
        >
          <track kind='captions' />
        </video>
      </VideoPlayerMainContainer>
    );
  }

  // On desktop use media-chrome
  return (
    <VideoPlayerMainContainer ref={containerRef} onMouseMove={handleMouseMove}>
      <MediaController ref={mediaControllerRef} style={{ width: '100%', aspectRatio: '16/9' }}>
        <ReactPlayer
          ref={reactPlayerRef}
          slot='media'
          src={src}
          controls={false}
          width='100%'
          height='100%'
          onEnded={onEnded}
          preload='metadata'
          autoPlay
          onStart={onReactPlayerReady}
          onTimeUpdate={onProgress}
          playing={playing}
        />

        {/* Full-size overlay with transition */}
        <VideoPlayerPanelControlsOverlay
          style={{
            ...wrapperControlsStyle,
            opacity: showControls ? 1 : 0,
            pointerEvents: showControls ? 'auto' : 'none',
          }}
        >
          {/* Close button (top-right) */}
          <Box display='flex' justifyContent='flex-end'>
            <Button onClick={handleCloseVideo} variant='link/primary'>
              <Svg name='close' size='m' />
            </Button>
          </Box>
          <Box flex={1} display='flex' justifyContent='center' alignItems='center'>
            {/* Center play/pause button */}
            <MediaPlayButton style={controlsDefaultStyle}>
              <span slot='play'>
                <Svg name='play' size='xxl' />
              </span>
              <span slot='pause'>
                <Svg name='pause' size='xxl' />
              </span>
            </MediaPlayButton>
          </Box>

          {/* Bottom controls */}
          <Box>
            <Box display='flex' flexDirection='column' width='100%'>
              <MediaTimeRange style={{ background: 'transparent', flex: 1, width: '100%' }} />
              <Box display='flex' flex={1} width='100%' justifyContent='space-between'>
                <Box>
                  <MediaPlayButton style={controlsDefaultStyle} />
                  <MediaMuteButton style={controlsDefaultStyle} />
                  <MediaVolumeRange style={controlsDefaultStyle} />
                  <MediaTimeDisplay showDuration style={controlsDefaultStyle} />
                </Box>
                <Box>
                  <MediaFullscreenButton style={controlsDefaultStyle} />
                  {/* <MediaPipButton style={controlsDefaultStyle} /> */}
                </Box>
              </Box>
            </Box>
          </Box>
        </VideoPlayerPanelControlsOverlay>
      </MediaController>
    </VideoPlayerMainContainer>
  );
};
